/*!
 * consent-gate.js
 * Default-deny gate for non-essential third-party scripts, network requests, cookies and storage.
 * Reads Consentmo's cookie `cookieconsent_preferences_disabled` (comma-separated list of denied
 * groups: functionality, analytics, marketing). No cookie = nothing accepted yet = everything denied.
 * Must be the first script in <head>.
 *
 * Public API:
 *   window.cgAllowed(category)            -> boolean
 *   window.cgLoad(category, src, attrs)   -> loads a script only if the category is allowed
 *
 * The page reloads once when the visitor changes their choice, so a clean page load always
 * reflects the current consent state.
 *
 * Only analytics and marketing are gated (client decision). Category 'necessary' lists
 * site features that always run (chat, wishlist, loyalty, sizing, shipping protection,
 * gift cards, app notifications). cgAllowed('necessary') is always true.
 *
 * Anything that does not match a rule is treated as necessary and never blocked
 * (Shopify, checkout, Global-e, Fast Simon, Klarna, Afterpay widget, Forter, Signifyd, Consentmo).
 */
(function (w, d) {
  'use strict';
  if (w.__cg) return;

  var COOKIE_NAME = 'cookieconsent_preferences_disabled';
  var CATS = ['functionality', 'analytics', 'marketing'];

  /* ---- URL rules (scripts, iframes, images, fetch, XHR, beacons) ---- */
  var URL_RULES = [
    /* Site features: never blocked */
    ['necessary', /(^|\.)(swym(\.it|cs\.com|app\.com|relay\.com|corp\.com)?|swymstore[a-z0-9-]*\.swymrelay\.com|gorgias\.(chat|io)|yotpo\.com|route\.com|kiwisizing\.com|boldmetrics\.io|d2hicexbdkkc9q\.cloudfront\.net|rise-ai\.com|mobiloud\.com)(\/|:|$)/i],
    ['analytics', /(^|\.)(logr-in\.com|lr-ingest\.io|lr-in\.com|logrocket\.(com|io)|getlimy\.ai|posthog\.com|amplitude\.com|google-analytics\.com|googletagmanager\.com|analytics\.google\.com|clarity\.ms|hotjar\.(com|io)|luckyorange\.(net|com))(\/|:|$)/i],
    ['marketing', /(^|\.)(klaviyo\.com|attn\.tv|attentivemobile\.com|jdphp\.seroya\.nyc|rakuten\.com|rakutenadvertising\.io|shopmy\.us|liadm\.com|facebook\.net|connect\.facebook\.net|tiktok\.com|tiktokw\.us|pinterest\.com|pinimg\.com\/ct|getgobot\.com|9gtb\.com|doubleclick\.net|googlesyndication\.com|googleadservices\.com|bat\.bing\.com|snapchat\.com|sc-static\.net)(\/|:|$)|facebook\.com\/tr|s3-us-west-2\.amazonaws\.com\/jsstore\//i],
    ['functionality', /(^|\.)(zooomyapps\.com|marker\.io)(\/|:|$)/i]
  ];

  /* ---- Cookie rules (document.cookie writes, and cleanup of old cookies) ---- */
  var COOKIE_RULES = [
    ['necessary', /^(swym|_swym|kiwi-sizing|_ks_|bm$|gorgias|gg_|_yotpo|yotpo)/i],
    ['analytics', /^(_ga($|_)|_gid$|_gat|_gac_|_hj|ph_|amp_|AMP_|_lr_|__lo|_lo_|lo_|_clck|_clsk|ttcsid)/],
    ['marketing', /^(_fbp|_fbc|_ttp|_tt_|_pin_|_pinterest|_derived_epik|_li_|lidid|_lc2_|_gcl|_gads|_uet|_scid|_sctr|_gb(sess|t|test)$|apt_pixel|__kla_|_kla_|__attentive|__attn|_attn|attntv|_ruid|_rsession|sms_aff)/],
    ['functionality', /^(mrkr)/i]
  ];

  /* ---- localStorage / sessionStorage rules ---- */
  var STORAGE_RULES = [
    ['necessary', /^(swym|gorgias\.|route_|route-|ROUTE_|__route|boldMetrics)/],
    ['analytics', /^(amp_|AMP_|amplitude_|EXP_|amp-exp-|ph_|__ph_|_lr_|__lr_)/],
    ['marketing', /^(_li_|_pin_|_gcl_ls|tt_|__kla_|_kla_|klaviyo|kl-|attn_|_attn|__attentive|__rm|_rsession$|_ruid$)/],
    ['functionality', /^(mrkr)/]
  ];

  /* ---- Consent state ---- */
  var cookieDesc = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ||
                   Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie');
  function rawGet() { return cookieDesc.get.call(d); }
  function rawSet(v) { cookieDesc.set.call(d, v); }

  function readCookie(name) {
    var m = rawGet().match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function readDenied() {
    var v = readCookie(COOKIE_NAME);
    if (v === null) return CATS.slice();
    return v.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  var denied = readDenied();
  var hits = 0;

  function isDenied(cat) { return !!cat && denied.indexOf(cat) !== -1; }
  function categoryFor(rules, str) {
    for (var i = 0; i < rules.length; i++) if (rules[i][1].test(str)) return rules[i][0];
    return null;
  }
  function urlBlocked(url) {
    if (!url) return false;
    var s = String(url);
    if (s.indexOf('data:') === 0 || s.indexOf('blob:') === 0) return false;
    var cat = categoryFor(URL_RULES, s.replace(/^[a-z]+:\/\//i, '').replace(/^\/\//, ''));
    if (cat && isDenied(cat)) { hits++; return true; }
    return false;
  }
  function cookieBlocked(name) {
    var cat = categoryFor(COOKIE_RULES, name);
    if (cat && isDenied(cat)) { hits++; return true; }
    return false;
  }
  function storageBlocked(key) {
    var cat = categoryFor(STORAGE_RULES, String(key));
    if (cat && isDenied(cat)) { hits++; return true; }
    return false;
  }

  /* ---- document.cookie guard ---- */
  try {
    Object.defineProperty(d, 'cookie', {
      configurable: true,
      get: function () { return rawGet(); },
      set: function (v) {
        var name = String(v).split('=')[0].trim();
        if (cookieBlocked(name)) return;
        rawSet(v);
      }
    });
  } catch (e) {}

  /* ---- Element src guards (dynamic script / iframe / image creation) ---- */
  function patchSrc(proto, isScript) {
    var desc = Object.getOwnPropertyDescriptor(proto, 'src');
    if (!desc || !desc.set) return;
    Object.defineProperty(proto, 'src', {
      configurable: true,
      enumerable: desc.enumerable,
      get: desc.get,
      set: function (v) {
        if (urlBlocked(v)) {
          try { this.setAttribute('data-cg-blocked-src', String(v)); } catch (e) {}
          if (isScript) { try { this.type = 'javascript/blocked'; } catch (e) {} }
          return;
        }
        desc.set.call(this, v);
      }
    });
  }
  patchSrc(HTMLScriptElement.prototype, true);
  patchSrc(HTMLIFrameElement.prototype, false);
  patchSrc(HTMLImageElement.prototype, false);

  var origSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function (name, value) {
    if (typeof name === 'string' && name.toLowerCase() === 'src') {
      var tag = this.tagName;
      if ((tag === 'SCRIPT' || tag === 'IFRAME' || tag === 'IMG') && urlBlocked(value)) {
        origSetAttribute.call(this, 'data-cg-blocked-src', String(value));
        if (tag === 'SCRIPT') origSetAttribute.call(this, 'type', 'javascript/blocked');
        return;
      }
    }
    return origSetAttribute.call(this, name, value);
  };

  /* ---- Network guards ---- */
  if (w.fetch) {
    var origFetch = w.fetch;
    w.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (urlBlocked(url)) return Promise.reject(new TypeError('Blocked by consent gate'));
      return origFetch.apply(this, arguments);
    };
  }
  if (w.XMLHttpRequest) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__cgBlocked = urlBlocked(url);
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__cgBlocked) { try { this.abort(); } catch (e) {} return; }
      return xhrSend.apply(this, arguments);
    };
  }
  if (navigator.sendBeacon) {
    var origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function (url) {
      if (urlBlocked(url)) return false;
      return origBeacon.apply(navigator, arguments);
    };
  }

  /* ---- Parser-inserted elements (scripts / iframes / images written in HTML, e.g. app embeds) ---- */
  function neutralise(n) {
    if (!n || n.nodeType !== 1) return;
    var tag = n.tagName;
    if (tag !== 'SCRIPT' && tag !== 'IFRAME' && tag !== 'IMG') return;
    var src = n.getAttribute('src');
    if (!src || !urlBlocked(src)) return;
    n.setAttribute('data-cg-blocked-src', src);
    n.removeAttribute('src');
    if (tag === 'SCRIPT') n.type = 'javascript/blocked';
  }
  try {
    new MutationObserver(function (list) {
      for (var i = 0; i < list.length; i++) {
        var added = list[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          neutralise(n);
          if (n.querySelectorAll) {
            var kids = n.querySelectorAll('script[src],iframe[src],img[src]');
            for (var k = 0; k < kids.length; k++) neutralise(kids[k]);
          }
        }
      }
    }).observe(d.documentElement, { childList: true, subtree: true });
  } catch (e) {}
  d.addEventListener('beforescriptexecute', function (e) {
    var s = e.target;
    if (s && s.src && urlBlocked(s.src)) e.preventDefault();
  }, true);

  /* ---- Storage guard ---- */
  try {
    var rawSetItem = w.Storage.prototype.setItem;
    w.Storage.prototype.setItem = function (k, v) {
      if (storageBlocked(k)) return;
      return rawSetItem.apply(this, arguments);
    };
  } catch (e) {}
  function purgeStorage() {
    ['localStorage', 'sessionStorage'].forEach(function (t) {
      try {
        var st = w[t];
        Object.keys(st).forEach(function (k) { if (storageBlocked(k)) st.removeItem(k); });
      } catch (e) {}
    });
  }

  /* ---- Cleanup of already-set cookies for denied groups ---- */
  function expireCookie(name) {
    var host = location.hostname.split('.');
    var domains = [null];
    for (var i = 0; i < host.length - 1; i++) domains.push('.' + host.slice(i).join('.'));
    domains.forEach(function (dom) {
      rawSet(name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + (dom ? '; domain=' + dom : ''));
    });
  }
  function purgeDenied() {
    rawGet().split('; ').forEach(function (c) {
      var name = c.split('=')[0];
      var cat = categoryFor(COOKIE_RULES, name);
      if (cat && isDenied(cat)) expireCookie(name);
    });
  }
  purgeDenied();
  purgeStorage();

  /* ---- React to consent changes: reload once so the page reflects the new state ---- */
  var snapshot = denied.slice().sort().join(',');
  function check() {
    var now = readDenied().sort().join(',');
    if (now === snapshot) return;
    snapshot = now;
    setTimeout(function () { location.reload(); }, 150);
  }
  setInterval(check, 400);
  d.addEventListener('visibilitychange', check);

  /* ---- Public API ---- */
  w.cgAllowed = function (cat) { return !isDenied(cat); };
  w.cgLoad = function (cat, src, attrs) {
    if (isDenied(cat)) { hits++; return null; }
    var s = d.createElement('script');
    s.async = true;
    if (attrs) for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) s.setAttribute(k, attrs[k]);
    s.src = src;
    var first = d.getElementsByTagName('script')[0];
    (first && first.parentNode ? first.parentNode : d.head).insertBefore(s, first || null);
    return s;
  };
  w.__cg = { denied: function () { return denied.slice(); }, hits: function () { return hits; } };
})(window, document);
